<template>
  <v-layout column class="custom-group lots-editor form-section-gap" no-gutters>
    <div class="label-with-tooltip">
      <span class="placeholder mb-0">Lots *</span>
      <AuctionFormInfoTooltip
        aria-label="Lots info"
        text="Split this auction into independent lots. Each lot has its own starting price and minimum bid step, and runs on the shared schedule below."
      />
    </div>

    <v-layout
      v-for="(lot, index) in lots"
      :key="lot.id"
      row
      wrap
      class="lot-row"
      no-gutters
      align-start
    >
      <v-flex xs12 sm12 md5 class="pr-md-4">
        <v-layout no-gutters class="form-field full-width-field" column justify-center align-start>
          <span class="placeholder">Lot title *</span>
          <input
            class="custom-input"
            type="text"
            :id="'lot-title-' + index"
            :value="lot.title"
            v-on:keydown.enter.prevent=""
            @input="updateLot(index, 'title', $event.target.value)"
            placeholder="Lot title"
          />
          <span v-if="showErrors && !lot.title" class="error-input">This field is required</span>
        </v-layout>
      </v-flex>
      <v-flex xs12 sm6 md4 class="pr-md-4">
        <v-layout no-gutters class="form-field full-width-field" column justify-center align-start>
          <span class="placeholder">Starting value *</span>
          <input
            class="custom-input"
            type="number"
            :id="'lot-value-' + index"
            :value="lot.value"
            v-on:keydown.enter.prevent=""
            @input="updateLot(index, 'value', numberOrNull($event.target.value))"
            placeholder="0"
          />
          <span v-if="showErrors && !(Number(lot.value) > 0)" class="error-input"
            >Enter a positive number</span
          >
        </v-layout>
      </v-flex>
      <v-flex xs9 sm5 md2 class="pr-md-2">
        <v-layout no-gutters class="form-field full-width-field" column justify-center align-start>
          <span class="placeholder">Minimal step *</span>
          <input
            class="custom-input"
            type="number"
            :id="'lot-minimal-step-' + index"
            :value="lot.minimalStep"
            v-on:keydown.enter.prevent=""
            @input="updateLot(index, 'minimalStep', numberOrNull($event.target.value))"
            placeholder="0"
          />
          <span v-if="showErrors && !isValidMinimalStep(lot.minimalStep)" class="error-input"
            >Enter a non-negative number</span
          >
        </v-layout>
      </v-flex>
      <v-flex xs3 sm1 md1 class="lot-row-remove">
        <v-icon v-if="lots.length > 1" small @click="removeLot(index)">delete</v-icon>
      </v-flex>
    </v-layout>

    <v-layout row no-gutters class="lots-editor-footer" justify-space-between align-center>
      <span class="add-more" @click="addLot">
        <v-icon small>add</v-icon>
        <span>Add lot</span>
      </span>
      <span v-if="lots.length" class="multilot-total-summary">
        Total starting price: <strong>{{ totalValueDisplay }} {{ currency }}</strong>
      </span>
    </v-layout>

    <span v-if="showErrors && lots.length === 0" class="error-input">
      At least one lot is required
    </span>
  </v-layout>
</template>

<script>
import { formatDisplayNumber } from '@/utils/numberFormat';
import AuctionFormInfoTooltip from '@/components/forms/auctionForm/AuctionFormInfoTooltip.vue';
import { generateLotId } from '@/components/forms/auctionForm/auctionFormPayload';

export default {
  name: 'AuctionFormLots',
  components: { AuctionFormInfoTooltip },
  props: {
    lots: { type: Array, required: true },
    currency: { type: String, default: '' },
    showErrors: { type: Boolean, default: false },
  },
  computed: {
    totalValueDisplay() {
      const total = this.lots.reduce((sum, lot) => sum + (Number(lot.value) || 0), 0);
      return formatDisplayNumber(total);
    },
  },
  methods: {
    numberOrNull(raw) {
      if (raw === '' || raw === null || raw === undefined) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    },
    isValidMinimalStep(value) {
      if (value === null || value === undefined || value === '') return false;
      const n = Number(value);
      return Number.isFinite(n) && n >= 0;
    },
    updateLot(index, field, value) {
      const next = this.lots.map((lot, i) => (i === index ? { ...lot, [field]: value } : lot));
      this.$emit('update:lots', next);
    },
    addLot() {
      const next = [
        ...this.lots,
        { id: generateLotId(), title: '', value: null, minimalStep: null },
      ];
      this.$emit('update:lots', next);
    },
    removeLot(index) {
      if (this.lots.length <= 1) return;
      const next = this.lots.slice();
      next.splice(index, 1);
      this.$emit('update:lots', next);
    },
  },
};
</script>

<style scoped>
.lots-editor {
  margin-top: 6px;
}

.lot-row {
  margin-bottom: 4px;
}

.lot-row-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  cursor: pointer;
}

.lots-editor-footer {
  margin-top: 4px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.multilot-total-summary {
  color: #216169;
  font-size: 12px;
  font-weight: 500;
}

.add-more {
  cursor: pointer;
}

.add-more span {
  font-weight: 600;
  margin-left: 8px;
}
</style>
